import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import { Theme, ThemeService } from '../../theme/theme.service';

@Component({
    selector: 'app-histogram',
    templateUrl: './histogram.component.html',
    styleUrls: ['./histogram.component.scss']
})
export class HistogramComponent implements OnInit, OnChanges {

    @Input() public title: string;
    @Input() public data: any;
    @Input() public xAxesLabel: string;
    @Input() public yAxesLabel: string;
    @Input() public colors: string[] | string;
    @Input() public barPercentage = 1.0;

    public chart: Chart;

    @ViewChild('chart', {static: true, read: ElementRef}) private readonly element: ElementRef;

    constructor(private readonly themes: ThemeService) {}

    private static defaultColor() {
        return '#c7d4f2';
    }

    private static sortAlphaNumberic(a, b) {
        return a.localeCompare(b, 'en', {numeric: true});
    }

    private darkThemeOptions() {
        return ({
            title: {
                display: true,
                fontColor: 'white',
                text: this.title
            },
            scales: {
                yAxes: [{
                    gridLines: {color: '#c2c2c2'},
                    ticks: {beginAtZero: true, stepSize: 1, fontColor: 'white'},
                    scaleLabel: {display: true, labelString: this.yAxesLabel, fontColor: 'white'}
                }],
                xAxes: [{gridLines: {display: false}, ticks: {fontColor: 'white'}, scaleLabel: {display: true, labelString: this.xAxesLabel, fontColor: 'white'}}]
            },
            legend: {
                display: false
            }
        });
    }

    private lightThemeOptions() {
        return ({
            title: {
                display: true,
                fontColor: 'black',
                text: this.title
            },
            scales: {
                yAxes: [{
                    gridLines: {color: '#c2c2c2'},
                    ticks: {beginAtZero: true, stepSize: 1, fontColor: 'black'},
                    scaleLabel: {display: true, labelString: this.yAxesLabel, fontColor: 'black'}
                }],
                xAxes: [{gridLines: {display: false}, ticks: {fontColor: 'black'}, scaleLabel: {display: true, labelString: this.xAxesLabel, fontColor: 'black'}}]
            },
            legend: {
                display: false
            }
        });
    }

    public ngOnInit(): void {
        const labels = this.data ? Object.keys(this.data) : [];
        labels.sort(HistogramComponent.sortAlphaNumberic);
        this.chart = new Chart(this.element.nativeElement, {
            type: 'bar',
            data: {
                labels,
                datasets: this.constructDatasets()
            }
        });

        this.themes.themeChanges.subscribe(this.updateForTheme.bind(this));
    }

    private updateForTheme(theme: Theme) {
        if (theme === Theme.Light) {
            this.chart.options = this.lightThemeOptions();
        } else if (theme === Theme.Dark) {
            this.chart.options = this.darkThemeOptions();
        }
        this.chart.update();
    }

    private constructDatasets() {
        const labels = this.data ? Object.keys(this.data) : [];
        labels.sort(HistogramComponent.sortAlphaNumberic);
        return this.data ? [{
            label: '',
            data: labels.map(key => this.data[key]),
            fill: true,
            borderWidth: 1,
            backgroundColor: this.colors ? this.colors : HistogramComponent.defaultColor,
            barPercentage: this.barPercentage
        }] : [];
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (this.chart && changes) {
            const labels = this.data ? Object.keys(this.data) : [];
            labels.sort(HistogramComponent.sortAlphaNumberic);
            this.chart.data = {
                labels,
                datasets: this.constructDatasets()
            };
            this.chart.update();
        }
    }

}
